import { Button, Divider, Select, Typography } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import styles from "./BusinessTableFilters.module.css";
import { TableParams } from "../../../../../interfaces/tableParams.interface";
import { useAppSelector } from "../../../../../app/hooks";
import { selectPartners } from "../../../../partners/partnersSlice";
import { selectHideUnreleasedAcquirers } from "../../../../feature-flags/featureFlagsSlice";
import {
  AcquirerStatusBucket,
  BusinessFeatureFilter,
  BusinessType,
  ComplianceStatus,
  FilterableAcquirer,
  IBusinessFullQuery,
} from "../../../../../interfaces/businessFull.interface";
import { KlearlyCountryAlpha2Code } from "../../../../../interfaces/klearlyCountryAlpha2Code.enum";
import { ACQUIRER_REVIEW_LABEL } from "../../../utils/mapStatusLabel";
import { debounce } from "lodash";
import {
  CheckOutlined,
  CloseOutlined,
  FilterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { pruneHiddenAcquirers } from "../../../utils/businessFilterParams";
import { getPartnersFromServer } from "../../../../partners/partnersApi";
import { IPartner } from "../../../../../interfaces/partner.interface";

const ACQUIRER_FILTER_META: Record<
  FilterableAcquirer,
  { dropdownLabel: string; tagLabel: string; color: string }
> = {
  [FilterableAcquirer.Rapyd]: {
    dropdownLabel: "Valitor",
    tagLabel: "Valitor",
    color: "#8c8c8c",
  },
  [FilterableAcquirer.Shift4]: {
    dropdownLabel: "Shift4",
    tagLabel: "Shift4",
    color: "#1677ff",
  },
  [FilterableAcquirer.Adyen]: {
    dropdownLabel: "Adyen",
    tagLabel: "Adyen",
    color: "#5b4cfc",
  },
};

const STATUS_FILTER_META: Record<
  AcquirerStatusBucket,
  { label: string; color: string; chipBg: string; chipFg: string }
> = {
  [AcquirerStatusBucket.PendingSubmission]: {
    label: "Pending Submission",
    color: "#bfbfbf",
    chipBg: "#f5f5f5",
    chipFg: "#595959",
  },
  [AcquirerStatusBucket.Submitted]: {
    label: "Submitted",
    color: "#1677ff",
    chipBg: "#e6f4ff",
    chipFg: "#1677ff",
  },
  [AcquirerStatusBucket.AcquirerReview]: {
    label: ACQUIRER_REVIEW_LABEL,
    color: "#1677ff",
    chipBg: "#e6f4ff",
    chipFg: "#1677ff",
  },
  [AcquirerStatusBucket.AwaitingMerchantAction]: {
    label: "Awaiting Merchant Action",
    color: "#fa8c16",
    chipBg: "#fff7e6",
    chipFg: "#d46b08",
  },
  [AcquirerStatusBucket.Approved]: {
    label: "Approved",
    color: "#52c41a",
    chipBg: "#f6ffed",
    chipFg: "#389e0d",
  },
  [AcquirerStatusBucket.Rejected]: {
    label: "Declined",
    color: "#ff4d4f",
    chipBg: "#fff2f0",
    chipFg: "#cf1322",
  },
};

interface BusinessTableFiltersProps {
  filters: Partial<IBusinessFullQuery>;
  setFilters: React.Dispatch<React.SetStateAction<Partial<IBusinessFullQuery>>>;
  setTableParams: React.Dispatch<React.SetStateAction<TableParams>>;
  onReset?: () => void;
}

export default function BusinessTableFilters({
  filters,
  setFilters,
  setTableParams,
  onReset,
}: BusinessTableFiltersProps) {
  const [isOpen, setIsOpen] = useState(true);
  const partners = useAppSelector(selectPartners);
  const [partnerOptions, setPartnerOptions] = useState<IPartner[]>(
    partners || []
  );
  const [partnerSearching, setPartnerSearching] = useState<boolean>(false);
  const [acquirerOpen, setAcquirerOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const hideUnreleasedAcquirers = useAppSelector(selectHideUnreleasedAcquirers);

  useEffect(() => {
    setPartnerOptions(partners || []);
  }, [partners]);

  const debouncedFetchPartners = useCallback(
    debounce(async (value: string) => {
      const query = value?.trim();
      if (!query) {
        setPartnerOptions(partners || []);
        setPartnerSearching(false);
        return;
      }
      setPartnerSearching(true);
      try {
        const res = await getPartnersFromServer({
          name: query,
          currentPage: 1,
          pageSize: 20,
        });
        setPartnerOptions(res?.data || []);
      } finally {
        setPartnerSearching(false);
      }
    }, 400),
    [partners]
  );

  // Back to page 1 but keep the page size the user picked.
  const resetPagination = () => {
    setTableParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, current: 1 },
    }));
  };

  const handleReset = () => {
    setFilters({});
    resetPagination();
    onReset?.();
  };

  const handleReachoutStatusChange = (value: ComplianceStatus | "all") => {
    setFilters((prev) => {
      if (!value || value === "all") {
        delete prev.complianceStatus;
        return { ...prev };
      }
      return { ...prev, complianceStatus: value };
    });
    resetPagination();
  };

  const handleTypeFilterChange = (value: BusinessType | "all") => {
    setFilters((prev) => {
      if (value === "all") {
        // @ts-ignore
        delete prev.businessType;
        return { ...prev };
      }
      return { ...prev, businessType: value };
    });
    resetPagination();
  };

  const handlePartnerIdFilterChange = (value: string | "all") => {
    setFilters((prev) => {
      if (value === "all") {
        delete prev.partnerId;
        return { ...prev };
      }
      return { ...prev, partnerId: value };
    });
    resetPagination();
  };

  const handleCountryFilterChange = (value?: string) => {
    setFilters((prev) => {
      if (!value || value === "all") {
        delete prev.country;
        return { ...prev };
      }
      return { ...prev, country: value };
    });
    resetPagination();
  };

  const handleFeatureFilterChange = (value: BusinessFeatureFilter[]) => {
    setFilters((prev) => {
      if (!value?.length) {
        delete prev.features;
        return { ...prev };
      }
      return { ...prev, features: value };
    });
    resetPagination();
  };

  // Stored comma-joined for the wire; the Selects need the array back.
  const splitFilterValue = (value?: string): string[] =>
    value ? value.split(",").filter(Boolean) : [];

  const setJoinedFilter = (
    key: "acquirers" | "acquirerStatuses",
    value: string[]
  ) => {
    setFilters((prev) => {
      if (!value?.length) {
        delete prev[key];
        return { ...prev };
      }
      return { ...prev, [key]: value.join(",") };
    });
    resetPagination();
  };

  const handleAcquirerFilterChange = (value: FilterableAcquirer[]) =>
    setJoinedFilter("acquirers", value);

  const handleAcquirerStatusFilterChange = (value: AcquirerStatusBucket[]) =>
    setJoinedFilter("acquirerStatuses", value);

  const visibleAcquirers = [
    FilterableAcquirer.Rapyd,
    ...(hideUnreleasedAcquirers ? [] : [FilterableAcquirer.Shift4]),
    FilterableAcquirer.Adyen,
  ];

  const storedAcquirers = splitFilterValue(
    filters.acquirers
  ) as FilterableAcquirer[];
  const selectedAcquirers = pruneHiddenAcquirers(
    storedAcquirers,
    visibleAcquirers
  ) as FilterableAcquirer[];

  // A bookmarked `acquirers=shift4` survives the dropdown filtering above, so
  // drop it from the stored filter too — that is what leaves in the request.
  useEffect(() => {
    if (storedAcquirers.length === selectedAcquirers.length) return;
    handleAcquirerFilterChange(selectedAcquirers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedAcquirers.length, selectedAcquirers.length]);
  const selectedStatuses = splitFilterValue(
    filters.acquirerStatuses
  ) as AcquirerStatusBucket[];

  const appliesToNames = (
    selectedAcquirers.length ? selectedAcquirers : visibleAcquirers
  )
    .map((value) => ACQUIRER_FILTER_META[value]?.tagLabel)
    .filter(Boolean)
    .join(", ");

  const renderDropdownFooter = (
    selectedCount: number,
    emptyLabel: string,
    onDone: () => void
  ) => (
    <div className={styles.dropdown_footer}>
      <span>{selectedCount ? `${selectedCount} selected` : emptyLabel}</span>
      <button
        type="button"
        className={styles.dropdown_done}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onDone}
      >
        Done
      </button>
    </div>
  );

  const renderAcquirerTag = ({ value }: { value: string }) => {
    const meta = ACQUIRER_FILTER_META[value as FilterableAcquirer];
    if (!meta) return <span />;
    return (
      <span className={styles.acquirer_tag}>
        <span className={styles.dot} style={{ background: meta.color }} />
        {meta.tagLabel}
      </span>
    );
  };

  const renderStatusTag = ({ value }: { value: string }) => {
    const meta = STATUS_FILTER_META[value as AcquirerStatusBucket];
    if (!meta) return <span />;
    return (
      <span
        className={styles.status_chip}
        style={{ background: meta.chipBg, color: meta.chipFg }}
      >
        <span className={styles.dot} style={{ background: meta.color }} />
        {meta.label}
      </span>
    );
  };

  return (
    <div className={isOpen ? styles.sidebar : styles.sidebar_collapsed}>
      <div
        className={styles.sidebar_header}
        onClick={() => setIsOpen((o) => !o)}
        style={{ cursor: "pointer" }}
      >
        {isOpen && (
          <Typography.Title level={5} style={{ margin: 0 }}>
            Filters
          </Typography.Title>
        )}
        <FilterOutlined style={{ fontSize: 16, color: "rgba(0,0,0,0.45)" }} />
      </div>

      {isOpen && (
        <>
          <Divider style={{ margin: "12px 0" }} />
          <Button
            type="link"
            icon={<CloseOutlined style={{ color: "#5b4cfc" }} />}
            onClick={handleReset}
            className={styles.reset_button}
          >
            Reset
          </Button>
          <Divider style={{ margin: "12px 0" }} />

          <div className={styles.filter_item}>
            <span className={styles.section_label}>
              Acquirer onboarding status
            </span>
            <Typography.Text
              type="secondary"
              className={styles.filter_label_vertical}
            >
              Acquirer
            </Typography.Text>
            <Select
              mode="multiple"
              showSearch={false}
              className={styles.acquirer_select}
              style={{ width: "100%", marginBottom: 8 }}
              placeholder="All acquirers"
              value={selectedAcquirers}
              open={acquirerOpen}
              onDropdownVisibleChange={setAcquirerOpen}
              onChange={(value) =>
                handleAcquirerFilterChange(value as FilterableAcquirer[])
              }
              tagRender={renderAcquirerTag}
              menuItemSelectedIcon={null}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  {renderDropdownFooter(
                    selectedAcquirers.length,
                    "Searches all acquirers",
                    () => setAcquirerOpen(false)
                  )}
                </>
              )}
              optionRender={(option) => {
                const value = option.value as FilterableAcquirer;
                const meta = ACQUIRER_FILTER_META[value];
                const checked = selectedAcquirers.includes(value);
                return (
                  <span className={styles.option_row}>
                    <span
                      className={`${styles.checkbox} ${
                        checked ? styles.checkbox_on : ""
                      }`}
                    >
                      {checked ? <CheckOutlined /> : null}
                    </span>
                    {meta?.dropdownLabel ?? option.label}
                  </span>
                );
              }}
              options={visibleAcquirers.map((value) => ({
                value,
                label: ACQUIRER_FILTER_META[value].dropdownLabel,
              }))}
            />
            <Typography.Text
              type="secondary"
              className={styles.filter_label_vertical}
            >
              Status
            </Typography.Text>
            <Select
              mode="multiple"
              showSearch={false}
              className={styles.acquirer_select}
              style={{ width: "100%" }}
              placeholder="All statuses"
              value={selectedStatuses}
              open={statusOpen}
              onDropdownVisibleChange={setStatusOpen}
              onChange={(value) =>
                handleAcquirerStatusFilterChange(
                  value as AcquirerStatusBucket[]
                )
              }
              tagRender={renderStatusTag}
              menuItemSelectedIcon={null}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  {renderDropdownFooter(
                    selectedStatuses.length,
                    "Searches all statuses",
                    () => setStatusOpen(false)
                  )}
                </>
              )}
              optionRender={(option) => {
                const value = option.value as AcquirerStatusBucket;
                const meta = STATUS_FILTER_META[value];
                const checked = selectedStatuses.includes(value);
                return (
                  <span className={styles.option_row}>
                    <span
                      className={`${styles.checkbox} ${
                        checked ? styles.checkbox_on : ""
                      }`}
                    >
                      {checked ? <CheckOutlined /> : null}
                    </span>
                    <span
                      className={styles.dot}
                      style={{ background: meta?.color }}
                    />
                    {meta?.label ?? option.label}
                  </span>
                );
              }}
              options={Object.values(AcquirerStatusBucket).map((value) => ({
                value,
                label: STATUS_FILTER_META[value].label,
              }))}
            />
            <span className={styles.applies_to}>
              Applies to <strong>{appliesToNames}.</strong>
            </span>
          </div>

          <Divider style={{ margin: "12px 0" }} />

          <span className={styles.section_label}>Business</span>

          <div className={styles.filter_item}>
            <Typography.Text
              type="secondary"
              className={styles.filter_label_vertical}
            >
              Reached Out
            </Typography.Text>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="All"
              value={(filters.complianceStatus as string) || undefined}
              onChange={(value: any) =>
                handleReachoutStatusChange(value || "all")
              }
              options={[
                { label: "All", value: "all" },
                {
                  label: "Not Reached Out",
                  value: ComplianceStatus.NotContacted,
                },
                { label: "Reached Out", value: ComplianceStatus.ReachedOut },
              ]}
            />
          </div>

          <div className={styles.filter_item}>
            <Typography.Text
              type="secondary"
              className={styles.filter_label_vertical}
            >
              Country
            </Typography.Text>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="All Countries"
              value={filters.country || undefined}
              onChange={(value) => handleCountryFilterChange(value || "all")}
              options={[
                { label: "All Countries", value: "all" },
                {
                  label: "Netherlands",
                  value: KlearlyCountryAlpha2Code.Netherlands,
                },
                { label: "Belgium", value: KlearlyCountryAlpha2Code.Belgium },
                { label: "Germany", value: KlearlyCountryAlpha2Code.Germany },
                { label: "France", value: KlearlyCountryAlpha2Code.France },
                { label: "Italy", value: KlearlyCountryAlpha2Code.Italy },
              ]}
            />
          </div>

          <div className={styles.filter_item}>
            <Typography.Text
              type="secondary"
              className={styles.filter_label_vertical}
            >
              Business Type
            </Typography.Text>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="All Types"
              value={(filters.businessType as string) || undefined}
              onChange={(value: any) => handleTypeFilterChange(value || "all")}
              options={[
                { label: "All Types", value: "all" },
                { label: "Sole Traders", value: "soleTrader" },
                { label: "Partnership", value: "partnership" },
                {
                  label: "Private Limited Company",
                  value: "privateLimitedCompany",
                },
                {
                  label: "Public Limited Company",
                  value: "publicLimitedCompany",
                },
                { label: "Other", value: "other" },
              ]}
            />
          </div>

          <div className={styles.filter_item}>
            <Typography.Text
              type="secondary"
              className={styles.filter_label_vertical}
            >
              Search Partners
            </Typography.Text>
            <Select
              showSearch
              allowClear
              filterOption={false}
              style={{ width: "100%", borderRadius: 20 }}
              placeholder="Partners"
              suffixIcon={<SearchOutlined />}
              onSearch={(value: string) => debouncedFetchPartners(value)}
              onChange={(value: any) =>
                handlePartnerIdFilterChange(value || "all")
              }
              notFoundContent={partnerSearching ? "Searching..." : null}
              value={(filters.partnerId as string) || undefined}
            >
              <Select.Option value="all">All</Select.Option>
              {partnerOptions.map((partner) => (
                <Select.Option key={partner._id} value={partner._id}>
                  {partner.name}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className={styles.filter_item}>
            <Typography.Text
              type="secondary"
              className={styles.filter_label_vertical}
            >
              Capabilities
            </Typography.Text>
            <Select
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              style={{ width: "100%" }}
              placeholder="All capabilities"
              value={filters.features}
              onChange={(value) =>
                handleFeatureFilterChange(value as BusinessFeatureFilter[])
              }
              options={[
                { label: "Pay At Table", value: BusinessFeatureFilter.Online },
                { label: "Pay By QR", value: BusinessFeatureFilter.PayByQR },
                { label: "Tips", value: BusinessFeatureFilter.Tips },
                { label: "Deposits", value: BusinessFeatureFilter.Deposits },
                { label: "Reviews", value: BusinessFeatureFilter.Reviews },
                { label: "Cash", value: BusinessFeatureFilter.Cash },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
}
